import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Bell, Calendar, ClockClockwise, MoonStars, PauseCircle, PaperPlaneTilt } from '@phosphor-icons/react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Loader2 } from 'lucide-react';
import { getMyNotificationPrefs, updateMyNotificationPrefs, previewMyDigest, applyAutoTerritory } from '../../services/api';

const CHOICES = [
  { value: 'daily', label: 'Quotidien', desc: 'Chaque jour à 9h UTC — tous les onboardings de la veille', icon: ClockClockwise, tone: 'from-[#00AEEF] to-[#2D5A4C]' },
  { value: 'weekly', label: 'Hebdomadaire', desc: 'Chaque lundi à 9h UTC — récapitulatif de la semaine (recommandé)', icon: Calendar, tone: 'from-[#80C51F] to-[#2D5A4C]' },
  { value: 'monthly', label: 'Mensuel', desc: 'Le 1er du mois — bilan mensuel condensé', icon: MoonStars, tone: 'from-[#FF8E00] to-[#D5007D]' },
  { value: 'off', label: 'Désactivé', desc: 'Aucun email — je consulte le tableau de bord Admin', icon: PauseCircle, tone: 'from-[#5E6D66] to-[#1E4136]' },
];

const TERRITORIES = [
  { value: 'guadeloupe', label: 'Guadeloupe' },
  { value: 'martinique', label: 'Martinique' },
  { value: 'guyane', label: 'Guyane' },
  { value: 'reunion', label: 'La Réunion' },
  { value: 'mayotte', label: 'Mayotte' },
  { value: 'saint-martin', label: 'Saint-Martin' },
  { value: 'saint-barthelemy', label: 'Saint-Barthélemy' },
  { value: 'polynesie', label: 'Polynésie' },
  { value: 'nouvelle-caledonie', label: 'Nouvelle-Calédonie' },
  { value: 'wallis-et-futuna', label: 'Wallis-et-Futuna' },
];

export function NotificationSettings() {
  const [pref, setPref] = useState('weekly');
  const [territories, setTerritories] = useState([]);
  const [userTerritory, setUserTerritory] = useState(null);
  const [autoSuggestion, setAutoSuggestion] = useState(null);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewEmail, setPreviewEmail] = useState('');

  const loadPrefs = () => {
    getMyNotificationPrefs()
      .then((r) => {
        setPref(r.data?.onboarding_digest || 'weekly');
        setTerritories(r.data?.territory_filter || []);
        setUserTerritory(r.data?.user_territory || null);
        setAutoSuggestion(r.data?.auto_territory_suggestion || null);
      })
      .catch(() => {});
  };

  useEffect(() => { loadPrefs(); }, []);

  const applyAuto = async () => {
    try {
      const r = await applyAutoTerritory();
      setTerritories(r.data.territory_filter);
      setAutoSuggestion(null);
      toast.success(`Filtre "Mon territoire" appliqué (${r.data.territory})`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Impossible d'appliquer");
    }
  };

  const save = async (val) => {
    const prev = pref;
    setPref(val);
    setSaving(true);
    try {
      await updateMyNotificationPrefs({ onboarding_digest: val });
      toast.success('Préférence enregistrée');
    } catch {
      setPref(prev);
      toast.error('Impossible de sauvegarder');
    }
    setSaving(false);
  };

  const toggleTerritory = async (t) => {
    const next = territories.includes(t) ? territories.filter((x) => x !== t) : [...territories, t];
    const prev = territories;
    setTerritories(next);
    try {
      await updateMyNotificationPrefs({ territory_filter: next });
      toast.success(next.length ? `Filtre : ${next.length} territoire(s)` : 'Filtre territoire retiré');
    } catch {
      setTerritories(prev);
      toast.error('Impossible de sauvegarder');
    }
  };

  const sendPreview = async (customTo) => {
    setPreview(true);
    try {
      const payload = { frequency: pref === 'off' ? 'weekly' : pref };
      if (customTo) payload.to = customTo;
      const r = await previewMyDigest(payload);
      toast.success(`Test envoyé à ${r.data.to} (${r.data.count} onboarding${r.data.count > 1 ? 's' : ''})`);
      setPreviewOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Envoi du test impossible");
    }
    setPreview(false);
  };

  return (
    <Card className="border-[#E8E6E1]" data-testid="notification-settings">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <Bell size={18} className="text-[#2D5A4C]" weight="duotone" />
          Digest onboarding
        </CardTitle>
        <p className="text-sm text-[#5E6D66]">Choisissez à quelle fréquence vous souhaitez recevoir un email récapitulatif des nouveaux membres ayant terminé le tour d'accueil.</p>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {CHOICES.map((c) => {
            const Icon = c.icon;
            const active = pref === c.value;
            return (
              <button
                key={c.value}
                onClick={() => save(c.value)}
                disabled={saving}
                className={`text-left rounded-xl border-2 p-4 transition-all ${active ? 'border-[#2D5A4C] bg-gradient-to-br from-[#2D5A4C]/5 to-[#00AEEF]/5 shadow-md' : 'border-[#E8E6E1] hover:border-[#2D5A4C]/40 bg-white'}`}
                data-testid={`digest-choice-${c.value}`}
              >
                <div className="flex items-start gap-3">
                  <div className={`p-2 rounded-lg bg-gradient-to-br ${c.tone} text-white shadow`}>
                    <Icon size={18} weight="fill" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className={`font-bold text-sm ${active ? 'text-[#2D5A4C]' : 'text-[#1A2421]'}`}>
                      {c.label}
                      {active && <span className="ml-2 text-[9px] uppercase bg-[#2D5A4C] text-white px-1.5 py-0.5 rounded-full">actif</span>}
                    </p>
                    <p className="text-[11px] text-[#5E6D66] mt-1 leading-relaxed">{c.desc}</p>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
        <div className="flex items-center justify-between gap-3 mt-5 pt-4 border-t border-[#E8E6E1]">
          <p className="text-[10px] text-[#5E6D66] italic flex-1">
            Astuce : les événements non lus restent toujours consultables dans la <a href="/notifications" className="text-[#2D5A4C] underline">Timeline notifications</a>, même si l'email est désactivé.
          </p>
          <Button
            variant="outline"
            size="sm"
            onClick={() => { setPreviewEmail(''); setPreviewOpen(true); }}
            disabled={preview}
            className="shrink-0 border-[#FF8E00] text-[#FF8E00] hover:bg-[#FF8E00] hover:text-white"
            data-testid="digest-preview-btn"
          >
            {preview ? <Loader2 size={12} className="mr-1.5 animate-spin" /> : <PaperPlaneTilt size={12} className="mr-1.5" weight="fill" />}
            Prévisualiser
          </Button>
        </div>

        {pref !== 'off' && (
          <div className="mt-5 pt-4 border-t border-[#E8E6E1]" data-testid="territory-filter">
            <p className="text-[11px] uppercase tracking-wider font-bold text-[#1A2421] mb-2">Filtre territoire (optionnel)</p>
            <p className="text-[11px] text-[#5E6D66] mb-3">Sélectionnez un ou plusieurs territoires pour ne recevoir que les onboardings des zones concernées (utile pour les responsables régionaux).</p>

            {autoSuggestion && (
              <div className="mb-3 flex items-center justify-between gap-2 px-3 py-2 rounded-lg bg-gradient-to-br from-[#80C51F]/10 to-[#00AEEF]/10 border border-[#80C51F]/40" data-testid="auto-territory-banner">
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-bold text-[#2D5A4C]">Nous avons détecté votre territoire dans votre profil</p>
                  <p className="text-[11px] text-[#5E6D66] capitalize">Territoire : <b>{autoSuggestion.replace(/-/g, ' ')}</b> — appliquer en un clic ?</p>
                </div>
                <Button
                  size="sm"
                  onClick={applyAuto}
                  className="bg-[#2D5A4C] hover:bg-[#1E4136] shrink-0"
                  data-testid="apply-auto-territory-btn"
                >
                  Appliquer « Mon territoire »
                </Button>
              </div>
            )}

            <div className="flex flex-wrap gap-2">
              {TERRITORIES.map((t) => {
                const active = territories.includes(t.value);
                const isUserTerritory = userTerritory === t.value;
                return (
                  <button
                    key={t.value}
                    onClick={() => toggleTerritory(t.value)}
                    className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-all relative ${active ? 'bg-[#2D5A4C] text-white shadow-md' : 'bg-white border border-[#E8E6E1] text-[#5E6D66] hover:border-[#2D5A4C]'}`}
                    data-testid={`territory-toggle-${t.value}`}
                    title={isUserTerritory ? 'Votre territoire (profil user)' : ''}
                  >
                    {active && <span className="mr-1">✓</span>}{t.label}
                    {isUserTerritory && (
                      <span className="absolute -top-1 -right-1 w-2.5 h-2.5 rounded-full bg-[#80C51F] border-2 border-white" title="Votre territoire"></span>
                    )}
                  </button>
                );
              })}
            </div>
            {territories.length > 0 && (
              <p className="text-[10px] text-[#2D5A4C] font-semibold mt-2">
                {territories.length} territoire{territories.length > 1 ? 's' : ''} filtré{territories.length > 1 ? 's' : ''}
              </p>
            )}
          </div>
        )}
      </CardContent>

      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-md" data-testid="digest-preview-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <PaperPlaneTilt size={16} weight="fill" className="text-[#FF8E00]" />
              Prévisualiser le digest
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-[#5E6D66]">
              Vous allez recevoir un test-email avec les données réelles de la période <b>{pref === 'off' ? 'hebdomadaire' : pref}</b>.
            </p>
            <div>
              <Label htmlFor="preview-target" className="text-xs">Adresse de destination (optionnel)</Label>
              <Input
                id="preview-target"
                type="email"
                value={previewEmail}
                onChange={(e) => setPreviewEmail(e.target.value)}
                placeholder="Laissez vide pour votre email"
                data-testid="digest-preview-target"
                className="mt-1"
              />
              <p className="text-[10px] text-[#5E6D66] italic mt-1">
                Envoyez à un collègue ou à votre email perso pour valider avant activation en prod.
              </p>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="ghost" onClick={() => setPreviewOpen(false)}>Annuler</Button>
            <Button
              onClick={() => sendPreview(previewEmail.trim() || undefined)}
              disabled={preview}
              className="bg-[#FF8E00] hover:bg-[#e07d00]"
              data-testid="digest-preview-send"
            >
              {preview ? <Loader2 size={12} className="mr-1.5 animate-spin" /> : <PaperPlaneTilt size={12} className="mr-1.5" weight="fill" />}
              Envoyer le test
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

export default NotificationSettings;
